import { useEffect, useRef, useState } from "react";
import { Download, X } from "lucide-react";
import { useTenants } from "@/hooks/useTenants";

interface ImportOrphanModalProps {
  open: boolean;
  node: string;
  vmid: number;
  vmName: string;
  onClose: () => void;
  onConfirm: (tenantId: number, serviceTier: "A" | "B" | "C") => void;
  loading?: boolean;
}

export function ImportOrphanModal({
  open,
  node,
  vmid,
  vmName,
  onClose,
  onConfirm,
  loading = false,
}: ImportOrphanModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { data: tenants } = useTenants();
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [serviceTier, setServiceTier] = useState<"A" | "B" | "C">("A");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      dialog.showModal();
      setTenantId(null);
      setServiceTier("A");
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  if (!open) return null;
  const ready = tenantId !== null;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto max-w-md rounded-xl border border-gray-700 bg-gray-800 p-0 text-gray-100 shadow-2xl backdrop:bg-black/60"
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Download className="h-6 w-6 text-blue-400" />
            <h3 className="text-lg font-semibold">Import VM into AlphaProx</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-4 text-sm text-gray-300">
          <p>
            Import <code className="rounded bg-gray-900 px-1 py-0.5 text-yellow-300">{vmName}</code>{" "}
            (vmid {vmid} on {node}) as a new row in <code className="rounded bg-gray-900 px-1 py-0.5">virtual_machines</code>.
            Cores, memory and disk size are introspected from <code className="rounded bg-gray-900 px-1 py-0.5">qm config</code>.
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              Tenant
            </label>
            <select
              value={tenantId ?? ""}
              onChange={(e) =>
                setTenantId(e.target.value ? Number(e.target.value) : null)
              }
              className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-400 focus:outline-none"
            >
              <option value="">— Select a tenant —</option>
              {(tenants ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              Service tier
            </label>
            <div className="flex gap-2">
              {(["A", "B", "C"] as const).map((tier) => (
                <button
                  key={tier}
                  type="button"
                  onClick={() => setServiceTier(tier)}
                  className={
                    "flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors " +
                    (serviceTier === tier
                      ? "border-blue-500 bg-blue-900/40 text-blue-200"
                      : "border-gray-600 bg-gray-900 text-gray-400 hover:bg-gray-700")
                  }
                >
                  Tier {tier}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => tenantId !== null && onConfirm(tenantId, serviceTier)}
            disabled={!ready || loading}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
